// Object Library — search thousands of CC0/CC-BY low-poly models
// (poly.pizza) and drop them into the world. Picked models are persisted to
// our own storage so worlds never depend on a third-party host.

import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Loader, ChevronLeft, ChevronRight, Box } from 'lucide-react';
import { searchModels, PolyModel } from '../services/polyPizzaApi';
import { importLibraryModel } from '../services/libraryImport';
import { PREFABS } from '../prefabs';
import { prefabThumb } from '../utils/prefabThumbs';
import { BUILDING_KINDS, generateBuilding } from '../buildings';
import { CITY_FLAVORS, cityGlbUrl, cityThumbUrl } from '../cityKit';
import { VENUES, venueThumbUrl } from '../venues';
import { builtinId, fetchHiddenBuiltins, fetchLibraryAssets, isEnvironmentAsset, LibraryAsset } from '../services/libraryCatalog';
import './PBRBrowser.css';

export interface PropAddOptions {
  seat?: boolean;
  collidable?: boolean;
  autoFit?: boolean;
}

interface ObjectLibraryBrowserProps {
  onClose: () => void;
  onAdd: (url: string, filename: string, fileType: string, opts?: PropAddOptions) => void;
  // Built-in prefabs spawn as groups of editable primitives, not GLBs.
  onAddPrefab?: (prefabId: string) => void;
  // City buildings: each placement is a freshly generated low-poly variation.
  onAddBuilding?: (kind: string) => void;
  // Venues place a GLB plus its sit markers as one group.
  onAddVenue?: (venueId: string) => void;
  // Curated items tagged `environment` (e.g. Conference Rooms) are whole
  // rooms: placed as an environment model (hidden colliders honoured), not a prop.
  onAddEnvironment?: (asset: LibraryAsset) => void;
}

const PAGE_SIZE = 24;

const ObjectLibraryBrowser: React.FC<ObjectLibraryBrowserProps> = ({ onClose, onAdd, onAddPrefab, onAddBuilding, onAddVenue, onAddEnvironment }) => {
  // Rendered once when the browser opens; cached across opens by prefabThumbs
  const [prefabThumbs] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.values(PREFABS).map((p) => [p.id, prefabThumb(p)]))
  );
  const [buildingThumbs] = useState<Record<string, string>>(() =>
    Object.fromEntries(BUILDING_KINDS.map((b) => [
      b.kind,
      prefabThumb({ id: `building-${b.kind}`, title: b.title, icon: b.icon, category: 'Furniture', parts: generateBuilding(b.kind).parts }),
    ]))
  );
  const [query, setQuery] = useState('');
  const [models, setModels] = useState<PolyModel[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  // Admin-curated GLBs + built-ins an admin has hidden (managed in /admin)
  const [curated, setCurated] = useState<LibraryAsset[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  useEffect(() => {
    fetchLibraryAssets().then(setCurated).catch(() => {});
    fetchHiddenBuiltins().then(setHidden).catch(() => {});
  }, []);
  const curatedCats = Array.from(new Set(curated.map((a) => a.category)));

  const runSearch = useCallback(async (q: string, p: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await searchModels(q, p, PAGE_SIZE);
      setModels(res.models);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.message || 'Search failed');
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runSearch('', 0); }, [runSearch]);

  let searchTimer: ReturnType<typeof setTimeout>;
  const onQueryChange = (value: string) => {
    setQuery(value);
    setPage(0);
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => runSearch(value, 0), 400);
  };

  const changePage = (delta: number) => {
    const next = Math.max(0, page + delta);
    setPage(next);
    runSearch(query, next);
  };

  const importModel = async (model: PolyModel) => {
    if (importingId) return;
    setImportingId(model.id);
    try {
      const url = await importLibraryModel(model);
      onAdd(url, model.title, 'model');
    } catch (err: any) {
      console.error('[ObjectLibrary] Import failed:', err);
      setError(err?.message || 'Import failed — try another model');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <div className="pbr-browser-overlay">
      <div className="pbr-browser-modal">
        <div className="pbr-browser-header">
          <h2><Box size={18} style={{ marginRight: 8, verticalAlign: '-3px' }} />Asset Library</h2>
          <button className="pbr-browser-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="pbr-browser-search">
          <Search size={15} />
          <input
            placeholder="Search models... chair, tree, spaceship, dog"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            autoFocus
          />
          {query && (
            <button className="pbr-search-clear" onClick={() => { setQuery(''); setPage(0); runSearch('', 0); }}>
              <X size={13} />
            </button>
          )}
        </div>

        {error && <div style={{ color: '#ff6b6b', fontSize: 13, padding: '8px 16px' }}>{error}</div>}

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {!query && !loading && onAddPrefab && (
          <div style={{ padding: '4px 16px 0' }}>
            {curated.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#3fecdb', margin: '6px 0 2px' }}>
                  GathrXR Library
                </div>
                {curatedCats.map((cat) => (
                  <div key={cat}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(234,246,248,0.55)', margin: '8px 0 6px' }}>{cat}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10, marginBottom: 4 }}>
                      {curated.filter((a) => a.category === cat).map((a) => (
                        <div
                          key={a.id}
                          onClick={() => (onAddEnvironment && isEnvironmentAsset(a)
                            ? onAddEnvironment(a)
                            : onAdd(a.url, a.name, 'model', a.collidable ? { collidable: true } : undefined))}
                          title={[a.name, a.tags?.length ? a.tags.join(', ') : ''].filter(Boolean).join(' — ')}
                          style={{
                            cursor: 'pointer', textAlign: 'center', padding: '6px', borderRadius: 10,
                            background: 'rgba(63,236,219,0.05)', border: '1px solid rgba(63,236,219,0.20)',
                          }}
                        >
                          {a.thumbnail_url ? (
                            <img src={a.thumbnail_url} alt={a.name} loading="lazy" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 6, background: 'rgba(8,21,39,0.6)' }} />
                          ) : (
                            <div style={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, background: 'rgba(8,21,39,0.6)' }}><Box size={28} /></div>
                          )}
                          <div style={{ fontSize: 11, color: 'rgba(234,246,248,0.85)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
            {onAddVenue && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#3fecdb', margin: '6px 0 2px' }}>
                  Venues — event spaces with sittable seating
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, margin: '8px 0 4px' }}>
                  {VENUES.filter((v) => !hidden.has(builtinId.venue(v.id))).map((v) => (
                    <div
                      key={v.id}
                      onClick={() => onAddVenue(v.id)}
                      title={`${v.title} — ${v.blurb}. ${v.roof}, ${v.seats.length} sittable seats. Keep it axis-aligned so collision stays tight.`}
                      style={{
                        cursor: 'pointer', padding: '6px', borderRadius: 10,
                        background: 'rgba(63,236,219,0.05)', border: '1px solid rgba(63,236,219,0.20)',
                      }}
                    >
                      <img
                        src={venueThumbUrl(v.id)}
                        alt={v.title}
                        loading="lazy"
                        style={{ width: '100%', aspectRatio: '1.35', objectFit: 'cover', borderRadius: 6, background: 'rgba(8,21,39,0.6)' }}
                      />
                      <div style={{ fontSize: 11.5, color: 'rgba(234,246,248,0.9)', marginTop: 5 }}>{v.title}</div>
                      <div style={{ fontSize: 10, color: 'rgba(234,246,248,0.5)' }}>
                        {v.size === 'S' ? 'Small' : 'Large'} · {v.roof} · {v.seats.length} seats
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#3fecdb', margin: '10px 0 2px' }}>
              City Kit — modelled buildings, real scale
            </div>
            {CITY_FLAVORS.map((flavor) => (
              <div key={flavor.id}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(234,246,248,0.55)', margin: '8px 0 6px' }}>
                  {flavor.title} <span style={{ fontWeight: 400, opacity: 0.6 }}>— {flavor.blurb}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10, marginBottom: 4 }}>
                  {flavor.buildings.filter((bld) => !hidden.has(builtinId.city(bld.id))).map((bld) => (
                    <div
                      key={bld.id}
                      onClick={() => onAdd(cityGlbUrl(bld.id), `${flavor.title} ${bld.label}`, 'model',
                                          { collidable: true, autoFit: false })}
                      title={`${bld.label} ${bld.variation} — modelled building at real scale. Keep it axis-aligned (0/90/180/270°) so collision stays tight.`}
                      style={{
                        cursor: 'pointer', textAlign: 'center', padding: '6px', borderRadius: 10,
                        background: 'rgba(63,236,219,0.05)', border: '1px solid rgba(63,236,219,0.20)',
                      }}
                    >
                      <img
                        src={cityThumbUrl(bld.id)}
                        alt={bld.label}
                        loading="lazy"
                        style={{ width: '100%', aspectRatio: '0.8', objectFit: 'cover', borderRadius: 6, background: 'rgba(8,21,39,0.6)' }}
                      />
                      <div style={{ fontSize: 11, color: 'rgba(234,246,248,0.85)', marginTop: 4 }}>
                        {bld.label} {bld.variation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#3fecdb', margin: '14px 0 2px' }}>
              GathrXR Props — editable pieces, sit-ready seats
            </div>
            {onAddBuilding && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(234,246,248,0.55)', margin: '8px 0 6px' }}>
                  Editable Buildings — primitive groups, new variation each stamp
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10, marginBottom: 4 }}>
                  {BUILDING_KINDS.filter((b) => !hidden.has(builtinId.building(b.kind))).map((b) => (
                    <div
                      key={b.kind}
                      onClick={() => onAddBuilding(b.kind)}
                      title={`${b.title} — generates a unique low-poly building each time you stamp; R rotates, Esc exits`}
                      style={{
                        cursor: 'pointer', textAlign: 'center', padding: '10px 6px', borderRadius: 10,
                        background: 'rgba(63,236,219,0.06)', border: '1px solid rgba(63,236,219,0.25)',
                      }}
                    >
                      {buildingThumbs[b.kind] ? (
                        <img src={buildingThumbs[b.kind]} alt={b.title} style={{ width: '100%', aspectRatio: '1', objectFit: 'contain', borderRadius: 6, background: 'rgba(8,21,39,0.6)' }} />
                      ) : (
                        <div style={{ fontSize: 30, lineHeight: '38px' }}>{b.icon}</div>
                      )}
                      <div style={{ fontSize: 11.5, color: 'rgba(234,246,248,0.85)', marginTop: 4 }}>{b.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(['Room Sets', 'Seating', 'Furniture', 'Architecture'] as const).map((cat) => (
              <div key={cat}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(234,246,248,0.55)', margin: '8px 0 6px' }}>{cat}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10, marginBottom: 4 }}>
                  {Object.values(PREFABS).filter((p) => p.category === cat && !hidden.has(builtinId.prefab(p.id))).map((p) => (
                    <div
                      key={p.id}
                      onClick={() => onAddPrefab(p.id)}
                      title={`${p.title} — spawns as a group; click any piece to recolor, Ungroup to break apart`}
                      style={{
                        cursor: 'pointer', textAlign: 'center', padding: '10px 6px', borderRadius: 10,
                        background: 'rgba(63,236,219,0.06)', border: '1px solid rgba(63,236,219,0.25)',
                      }}
                    >
                      {prefabThumbs[p.id] ? (
                        <img
                          src={prefabThumbs[p.id]}
                          alt={p.title}
                          style={{ width: '100%', aspectRatio: '1', objectFit: 'contain', borderRadius: 6, background: 'rgba(8,21,39,0.6)' }}
                        />
                      ) : (
                        <div style={{ fontSize: 30, lineHeight: '38px' }}>{p.icon}</div>
                      )}
                      <div style={{ fontSize: 11.5, color: 'rgba(234,246,248,0.85)', marginTop: 4 }}>{p.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pbr-browser-grid" style={{ flex: 'unset', overflowY: 'visible' }}>
          {loading ? (
            <div className="pbr-browser-loading"><Loader size={22} className="pbr-spin" style={{ animation: 'spin 1s linear infinite' }} /> Searching...</div>
          ) : models.length === 0 ? (
            <div className="pbr-browser-loading">No models found — try another search</div>
          ) : (
            models.map((m) => (
              <div
                key={m.id}
                className="pbr-browser-item"
                onClick={() => importModel(m)}
                title={`${m.title} — by ${m.creator}${m.triCount ? ` · ${(m.triCount / 1000).toFixed(1)}k tris` : ''}`}
              >
                <div style={{ position: 'relative', width: '100%', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {m.thumbnail ? <img src={m.thumbnail} alt={m.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Box size={32} />}
                  {importingId === m.id && (
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.55)', color: '#00c2b3', fontSize: 12, gap: 6,
                    }}>
                      <Loader size={14} className="pbr-spin" /> Adding...
                    </div>
                  )}
                </div>
                <div className="pbr-item-label">{m.title}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.creator}</div>
              </div>
            ))
          )}
        </div>

        </div>

        <div className="pbr-browser-footer">
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
            CC0 / CC-BY models via poly.pizza — free to use, attribution shown in tooltip
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#fff', cursor: 'pointer' }} disabled={page === 0 || loading} onClick={() => changePage(-1)}><ChevronLeft size={15} /></button>
            <span style={{ fontSize: 12 }}>Page {page + 1}</span>
            <button style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#fff', cursor: 'pointer' }} disabled={loading || (page + 1) * PAGE_SIZE >= total} onClick={() => changePage(1)}><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectLibraryBrowser;
